import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Button, Grid, Link, Stack, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-pro'
import AddIcon from '@mui/icons-material/Add'
import { useGetCustomersQuery, usePatchCustomerMutation } from 'services/customers'
import DataGridWithActionButtons from 'components/generic/datagrid/DataGridWithActionButtons'
import AddCustomerModal from 'components/generic/modals/dialog-modal/AddCustomerModal'
import Loading from 'components/generic/Loading'
import { dispatchSnackbar } from 'utils/helpers'

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnsContainer': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: 'none',
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const ZendeskLink = (
  <Link target="_blank" href="https://intercom.help/greenspark-software/en/articles/6896170-price-lists">
    here
  </Link>
)

const PriceListCustomers = ({ selectedPriceList }) => {
  const classes = useStyles()
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()

  const { data: customersData, isLoading: isLoadingCustomersData } = useGetCustomersQuery(
    { filter: `{"priceSheetId":"${selectedPriceList.id}"}` },
    { skip: !selectedPriceList?.id },
  )

  const [patchCustomer, { isLoading: isUpdatingCustomer }] = usePatchCustomerMutation()

  const isLoading = isLoadingCustomersData || isUpdatingCustomer
  const [individualCustomers, setIndividualCustomers] = useState([])
  const [openModal, setOpenModal] = useState(false)

  useEffect(() => {
    if (customersData) {
      setIndividualCustomers(
        (customersData?.Items || []).map(customer => ({
          id: customer.id,
          name: customer.customerCommonIdentifierString,
        })),
      )
    }
  }, [customersData])

  const handleDeleteClick = useCallback(
    id => async event => {
      event.stopPropagation()
      // set priceSheetId = null so that customer is removed from price sheet
      await patchCustomer({
        body: { priceSheetId: null },
        customerId: id,
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', 'Customer deleted successfully.')
          apiRef.current?.updateRows([{ id, _action: 'delete' }])
        })
        .catch(error => {
          const errorMessage =
            error?.data?.error || 'An error occurred while deleting the customer from the price list.'
          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    },
    [apiRef],
  )

  const onSave = useCallback(
    async customer => {
      setOpenModal(false)

      await patchCustomer({
        body: { priceSheetId: selectedPriceList.id },
        customerId: customer.id,
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', 'Customer added successfully.')
        })
        .catch(error => {
          const errorMessage = error?.data?.error || 'An error occurred while adding the customer to the price list.'
          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    },
    [apiRef],
  )

  const editActionsLeft = [
    {
      text: 'Add Customer',
      variant: 'outlined',
      id: 'add-button',
      icon: <AddIcon />,
      onClick: () => setOpenModal(true),
    },
  ]

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Individual Customers',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        type: 'actions',
        getActions: params => [
          <GridActionsCellItem onClick={handleDeleteClick(params.row.id)} label="Delete" showInMenu />,
        ],
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    ],
    [],
  )

  return (
    <>
      {isLoading && <Loading />}
      <Grid p={3} flexDirection="column">
        {individualCustomers.length === 0 && (
          <Stack flexDirection="column" alignItems="center">
            <Typography color="text.secondary" variant="body1">
              Add customers to this price list. Learn more about price lists {ZendeskLink}.
            </Typography>
            <Button sx={{ mt: 2 }} size="small" {...editActionsLeft[0]} startIcon={editActionsLeft[0].icon}>
              Add Customer
            </Button>
          </Stack>
        )}
        {individualCustomers.length > 0 && (
          <DataGridWithActionButtons
            className={classes.root}
            leftActionButtons={editActionsLeft}
            height="auto"
            pagination={false}
            rows={individualCustomers}
            width="300px"
            loading={isLoading}
            columns={columns}
            disableColumnMenu
            onCellClick={(params, event) => {
              event.stopPropagation()

              apiRef.current.state.focus.cell = null
            }}
            hideFooter
            apiRef={apiRef}
          />
        )}
      </Grid>
      <AddCustomerModal
        title="Add Customers"
        buttonTitle="Add"
        hideDividers
        open={openModal}
        onSave={onSave}
        onClose={() => setOpenModal(false)}
      />
    </>
  )
}

PriceListCustomers.propTypes = {
  selectedPriceList: PropTypes.object.isRequired,
}

export default PriceListCustomers
